// photoEdit.js – Bildausschnitt, Drehung und Farbkorrektur (nicht-destruktiv)
//
// Das Originalfoto bleibt unverändert gespeichert (person.photo). Die Bearbeitung
// liegt in person.photoEdit:
//   { w, h,          natürliche Grösse des Originals (px)
//     rot,           Drehung in Grad (90°-Schritte + Feinkorrektur)
//     cx, cy, cs,    quadratischer Ausschnitt im gedrehten Bild (Ecke oben links + Seitenlänge, px)
//     b, c, s }      Helligkeit, Kontrast, Sättigung (1 = unverändert)

// Neutrale Farbwerte: Helligkeit, Kontrast und Sättigung unverändert.
export const DEFAULT_ADJUST = { b: 1, c: 1, s: 1 };

// Grösse der Fläche, die das gedrehte Bild einnimmt
// Ein gedrehtes Rechteck braucht mehr Platz (die Ecken ragen hinaus).
// Hier wird die Breite rw und Höhe rh dieser grösseren Fläche berechnet.
export function rotatedBox(w, h, rot) {
  const r = (rot * Math.PI) / 180;
  const cos = Math.abs(Math.cos(r)), sin = Math.abs(Math.sin(r));
  return { rw: w * cos + h * sin, rh: w * sin + h * cos };
}

// Ausschnitt innerhalb des gedrehten Bildes halten
// Sorgt dafür, dass der quadratische Ausschnitt nie über das Bild hinausragt und
// weder zu gross noch winzig ist (mindestens 80 px bzw. 8 % der Bildgrösse).
export function clampEdit(e) {
  const { rw, rh } = rotatedBox(e.w, e.h, e.rot);
  const maxS = Math.min(rw, rh);
  const minS = Math.min(maxS, Math.max(80, maxS * 0.08));
  const cs = Math.min(maxS, Math.max(minS, e.cs));
  const cx = Math.min(rw - cs, Math.max(0, e.cx));
  const cy = Math.min(rh - cs, Math.max(0, e.cy));
  return { ...e, cs, cx, cy };
}

// Vorschlag ohne Gesichtserkennung: Quadrat mittig, bei Hochformat etwas nach oben
export function defaultEdit(w, h) {
  const cs = Math.min(w, h);
  const cx = (w - cs) / 2;
  const cy = h > w ? Math.max(0, (h - cs) * 0.25) : (h - cs) / 2;
  return { w, h, rot: 0, cx, cy, cs, ...DEFAULT_ADJUST };
}

// Vorschlag anhand eines erkannten Gesichts (Box im Originalbild)
// Ausschnitt um das Gesicht: etwa doppelt so gross wie das Gesicht, das Gesicht
// leicht oberhalb der Mitte (wirkt natürlicher, Platz für Hals und Schultern).
export function editFromFace(w, h, face) {
  const cs = Math.min(Math.min(w, h), Math.max(face.width * 2.1, face.height * 2.0));
  const fx = face.x + face.width / 2;
  const fy = face.y + face.height / 2;
  return clampEdit({ w, h, rot: 0, cx: fx - cs / 2, cy: fy - cs * 0.46, cs, ...DEFAULT_ADJUST });
}

// CSS-Filter für die Vorschau im Browser, z.B. „brightness(1.1) contrast(1.05)“.
// undefined = kein Filter nötig.
export function filterCss(e) {
  const b = e?.b ?? 1, c = e?.c ?? 1, s = e?.s ?? 1;
  if (b === 1 && c === 1 && s === 1) return undefined;
  return `brightness(${b}) contrast(${c}) saturate(${s})`;
}

// Platzierung des Bildes in einem quadratischen Rahmen – in Prozent der Rahmenbreite
// Rechnet aus, wo das (gedrehte) Bild liegen muss, damit genau der gewählte
// Ausschnitt im quadratischen Rahmen sichtbar ist. Angaben in Prozent, damit es
// bei jeder Grösse passt. Verwendet von EditedPhoto.jsx.
//   layer = Fläche des gedrehten Bildes, verschoben um den Ausschnitt
//   img   = das Originalbild darin, mittig und gedreht
export function layoutPercent(e) {
  const { rw, rh } = rotatedBox(e.w, e.h, e.rot);
  const k = 100 / e.cs;
  return {
    layer: { left: `${-e.cx * k}%`, top: `${-e.cy * k}%`, width: `${rw * k}%`, height: `${rh * k}%` },
    img: {
      left: `${((rw - e.w) / 2 / rw) * 100}%`,
      top: `${((rh - e.h) / 2 / rh) * 100}%`,
      width: `${(e.w / rw) * 100}%`,
      height: `${(e.h / rh) * 100}%`,
      transform: e.rot ? `rotate(${e.rot}deg)` : undefined,
      filter: filterCss(e),
    },
  };
}

// ── Bild laden (mit Cache) ─────────────────────────────────────────────
// Bereits geladene Bilder merken, damit dasselbe Foto nicht mehrmals geladen wird.
// Höchstens 80 Bilder, danach fliegt das älteste raus.
const imgCache = new Map();
export function loadImage(src) {
  if (!src) return Promise.reject(new Error('kein Bild'));
  if (imgCache.has(src)) return imgCache.get(src);
  // Ein „Promise“ ist ein Versprechen auf ein späteres Ergebnis: Es wird erfüllt
  // (resolve), sobald das Bild geladen ist, oder schlägt fehl (reject).
  const p = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
  imgCache.set(src, p);
  if (imgCache.size > 80) imgCache.delete(imgCache.keys().next().value);
  return p;
}

// Farbkorrektur pixelweise (gleiche Formeln wie CSS-Filter → überall gleiches Ergebnis)
// Wendet Helligkeit (b), Kontrast (c) und Sättigung (s) auf jeden Bildpunkt an.
// data enthält pro Pixel 4 Zahlen: Rot, Grün, Blau, Transparenz.
// Die Formeln entsprechen den CSS-Filtern, damit Vorschau und Druck gleich aussehen.
function applyAdjust(data, b, c, s) {
  if (b === 1 && c === 1 && s === 1) return;
  // Gewichtung der Farben für die Helligkeit (das Auge ist für Grün am empfindlichsten).
  const lr = 0.2126, lg = 0.7152, lb = 0.0722;
  // Farbmatrix für die Sättigung (s=0 → Graustufen, s=1 → unverändert).
  const m = [
    lr + (1 - lr) * s, lg - lg * s,       lb - lb * s,
    lr - lr * s,       lg + (1 - lg) * s, lb - lb * s,
    lr - lr * s,       lg - lg * s,       lb + (1 - lb) * s,
  ];
  for (let i = 0; i < data.length; i += 4) {
    // Helligkeit: jeden Farbwert multiplizieren.
    let r = data[i] * b, g = data[i + 1] * b, bl = data[i + 2] * b;
    // Kontrast: Abstand zur Mitte (127.5) vergrössern oder verkleinern.
    r = (r - 127.5) * c + 127.5; g = (g - 127.5) * c + 127.5; bl = (bl - 127.5) * c + 127.5;
    data[i]     = m[0] * r + m[1] * g + m[2] * bl;
    data[i + 1] = m[3] * r + m[4] * g + m[5] * bl;
    data[i + 2] = m[6] * r + m[7] * g + m[8] * bl;
  }
}

// Bearbeitetes, quadratisches Foto als Canvas (size = Kantenlänge in px)
// Zeichnet das bearbeitete Foto auf eine Zeichenfläche („Canvas“):
// weisser Hintergrund, verschieben, drehen, zeichnen, dann Farbkorrektur.
// size = Kantenlänge des Ergebnisses in Pixeln.
export function renderToCanvas(img, e, size, { adjust = true } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, size, size);
  const { rw, rh } = rotatedBox(e.w, e.h, e.rot);
  // k = Vergrösserungsfaktor vom Ausschnitt auf die Zielgrösse.
  const k = size / e.cs;
  ctx.imageSmoothingQuality = 'high';
  ctx.save();
  // Erst so verschieben, dass der Ausschnitt oben links beginnt, dann um die
  // Bildmitte drehen.
  ctx.translate(-e.cx * k, -e.cy * k);
  ctx.translate((rw * k) / 2, (rh * k) / 2);
  ctx.rotate((e.rot * Math.PI) / 180);
  ctx.drawImage(img, (-e.w * k) / 2, (-e.h * k) / 2, e.w * k, e.h * k);
  ctx.restore();
  if (adjust) {
    const d = ctx.getImageData(0, 0, size, size);
    applyAdjust(d.data, e.b ?? 1, e.c ?? 1, e.s ?? 1);
    ctx.putImageData(d, 0, 0);
  }
  return canvas;
}

// Bearbeitetes Foto als JPEG-Daten-URL (für Vorschau und Download)
const renderCache = new Map();
// Wie renderToCanvas, liefert aber gleich ein JPEG als Text („data:image/jpeg;base64,…“),
// das man in ein <img> stecken kann. Ergebnisse werden zwischengespeichert.
export async function renderEditedDataUrl(photo, edit, size, quality = 0.9) {
  const img = await loadImage(photo);
  const e = edit || defaultEdit(img.naturalWidth, img.naturalHeight);
  // Nie grösser rechnen als der Ausschnitt im Original ist (sonst nur unscharf und gross).
  const out = Math.min(size, Math.round(e.cs)) || size;
  const key = `${photo.length}:${photo.slice(-40)}|${JSON.stringify(e)}|${out}`;
  if (renderCache.has(key)) return renderCache.get(key);
  const url = renderToCanvas(img, e, out).toDataURL('image/jpeg', quality);
  renderCache.set(key, url);
  if (renderCache.size > 60) renderCache.delete(renderCache.keys().next().value);
  return url;
}

// ── Automatisch verbessern ─────────────────────────────────────────────
// Misst Helligkeit, Kontrastumfang und Farbigkeit im Ausschnitt und schlägt
// Werte vor, mit denen das Foto weder zu dunkel noch flau wirkt.
// Die Werte werden an einem kleinen Vorschaubild (160 × 160) gemessen – das ist schnell.
export function autoAdjust(img, e) {
  const c = renderToCanvas(img, { ...e, ...DEFAULT_ADJUST }, 160, { adjust: false });
  const d = c.getContext('2d').getImageData(0, 0, 160, 160).data;
  // Histogramm: wie viele Pixel welche Helligkeit (0–255) haben.
  const hist = new Array(256).fill(0);
  let sum = 0, chroma = 0, n = 0;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i], g = d[i + 1], b = d[i + 2];
    const l = Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b);
    hist[l]++; sum += l; n++;
    chroma += Math.max(r, g, b) - Math.min(r, g, b);
  }
  // pct(0.01) = Helligkeit, unter der 1 % der Pixel liegen (die dunkelsten), pct(0.99) entsprechend die hellsten.
  const pct = (p) => { let acc = 0; for (let i = 0; i < 256; i++) { acc += hist[i]; if (acc >= n * p) return i; } return 255; };
  const lo = pct(0.01), hi = pct(0.99);
  // Kontrast so anheben, dass die Helligkeiten fast die ganze Skala nutzen (höchstens +30 %).
  const contrast = Math.min(1.3, Math.max(1, 225 / Math.max(40, hi - lo)));
  // Mittelwert nach Kontrast-Anpassung → Helligkeit Richtung 50–55 %
  const mean = sum / n;
  const meanAfter = (mean - 127.5) * contrast + 127.5;
  const brightness = Math.min(1.35, Math.max(0.85, 135 / Math.max(30, meanAfter)));
  const avgChroma = chroma / n;
  // Sättigung: Graue Bilder bleiben grau, blasse werden etwas kräftiger.
  const saturation = avgChroma < 12 ? 1 : avgChroma < 40 ? 1.15 : avgChroma < 70 ? 1.08 : 1.02;
  const round = (v) => Math.round(v * 100) / 100;
  return { b: round(brightness), c: round(contrast), s: round(saturation) };
}

// ── Gesichtserkennung (wird erst beim ersten Gebrauch geladen) ─────────
// Die Gesichtserkennung (face-api) ist gross. Sie wird erst geladen, wenn sie
// das erste Mal gebraucht wird, und danach wiederverwendet. Das Erkennungsmodell
// liegt im Ordner public/models.
let faceApiPromise = null;
function loadFaceApi() {
  if (!faceApiPromise) {
    faceApiPromise = import('@vladmandic/face-api').then(async (faceapi) => {
      await faceapi.nets.tinyFaceDetector.loadFromUri(`${process.env.PUBLIC_URL || ''}/models`);
      return faceapi;
    }).catch((err) => { faceApiPromise = null; throw err; });
  }
  return faceApiPromise;
}

// Grösstes Gesicht im Bild finden → Box in Originalpixeln, oder null
export async function detectFace(img) {
  try {
    const faceapi = await loadFaceApi();
    // Für die Erkennung reicht ein verkleinertes Bild (höchstens 640 px) – viel schneller.
    const scale = Math.min(1, 640 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    const found = await faceapi.detectAllFaces(c, new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.45 }));
    if (!found.length) return null;
    // Mehrere Gesichter? Das grösste nehmen.
    const best = found.reduce((a, f) => (f.box.width * f.box.height > a.box.width * a.box.height ? f : a));
    const bx = best.box;
    // Zurückrechnen auf die Grösse des Originalbildes.
    return { x: bx.x / scale, y: bx.y / scale, width: bx.width / scale, height: bx.height / scale };
  } catch {
    return null; // Erkennung nicht möglich → einfache Regel
  }
}

// ── Namen und Datum für die Karten ─────────────────────────────────────
// Vor- und Nachname einer Person. Neuere Daten haben firstName/lastName,
// ältere nur name – dann gilt das letzte Wort als Nachname.
export function splitName(p) {
  if (p?.firstName != null || p?.lastName != null) {
    return { first: (p.firstName || '').trim(), last: (p.lastName || '').trim() };
  }
  const parts = String(p?.name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: parts[0] || '', last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

// „1980-05-17“ → „17·05·1980“ (so steht das Datum auf der Namenskarte).
export function cardDate(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}·${m[2]}·${m[1]}` : String(iso);
}

// Druck: Karte 6 × 6 cm, Fotofeld 72,7 % → 43,6 mm.
// Bei 300 dpi braucht es ~515 px, unter ~350 px wird es sichtbar unscharf.
export const PRINT_PX_GOOD = 500;
export const PRINT_PX_MIN = 350;
